/**
 * Utility functions for content references
 * @module content-detail/content-references/utils
 */

import type {
  ContentReference,
  ContentReferenceMap,
  CompareReferencesResult,
  ContentReferencesSectionContentData,
  GraphQLContentReference,
} from '../types'

/** Input shape for createContentReferences mutation (one item in the array). */
export interface ContentReferenceCreateInput {
  fromContentContentId: number | undefined
  fromContentContentType: string | undefined
  toContentContentId: number
  toContentContentType: string
  type: string
  sortKey: null
}

/** Result of computing what to delete and create when saving content references. */
export interface ContentReferenceSavePayload {
  toDelete: ContentReference[]
  toCreate: ContentReferenceCreateInput[]
}

/**
 * Transform GraphQL content references to component-friendly format
 *
 * @param graphqlReferences - Content references from GraphQL query
 * @returns Transformed references for component use
 *
 * @example
 * // Input from GraphQL:
 * [{
 *   id: 12345,
 *   contentReferenceType: { type: "admin_category" },
 *   contentTo: {
 *     id: 67890,
 *     contentId: 789,
 *     contentType: "tag",
 *     title: "Yoga",
 *     published: true,
 *     titleGroupId: "tag-789"
 *   }
 * }]
 *
 * // Output for component:
 * [{
 *   id: 67890,
 *   contentId: 789,
 *   contentType: "tag",
 *   title: "Yoga",
 *   published: true,
 *   titleGroupId: "tag-789",
 *   referenceId: 12345,  // ← ContentReference.id needed for deletion!
 *   contentReferenceType: "admin_category"  // ← Reference type for grouping
 * }]
 */
export function transformGraphQLToComponent (
  graphqlReferences: GraphQLContentReference[],
): ContentReference[] {
  if (!Array.isArray(graphqlReferences)) {
    return []
  }

  const filtered = graphqlReferences.filter(
    (ref) => ref && ref.contentTo && ref.contentTo.length > 0,
  )

  return filtered.map((ref) => {
    // contentTo is an array, get the first element
    const contentToItem = ref.contentTo![0]!
    return {
      // Content fields for display
      id: contentToItem.id,
      contentId: contentToItem.contentId,
      contentType: contentToItem.contentType,
      title: contentToItem.title,
      published: contentToItem.published,
      titleGroupId: contentToItem.titleGroupId,
      // Reference ID for deletion
      referenceId: ref.id,
      // Reference type for grouping/filtering
      contentReferenceType: ref.contentReferenceType?.type || '',
    }
  })
}

/**
 * Compare initial and current content references to determine changes
 *
 * @param initial - Initial references from GraphQL
 * @param current - Current references from user changes
 * @returns Object with additions and deletions arrays
 *
 * @example
 * const { additions, deletions } = compareReferences(initial, current)
 *
 * // additions: New items to create (no referenceId)
 * // deletions: Removed items to delete (has referenceId)
 */
export function compareReferences (
  initial: ContentReference[] = [],
  current: ContentReference[] = [],
): CompareReferencesResult {
  // Find additions: items in current but not in initial
  // Compare by contentId (unique identifier for content)
  const additions = current.filter(
    (curr) => !initial.some((init) => init.contentId === curr.contentId),
  )

  // Find deletions: items in initial but not in current
  // IMPORTANT: Deletions must include the referenceId for deletion mutation
  const deletions = initial.filter(
    (init) => !current.some((curr) => curr.contentId === init.contentId),
  )

  return {
    additions,
    deletions,
  }
}

/**
 * Check if two reference arrays are equal (by contentId)
 * Useful for dirty state detection
 *
 * @param arr1 - First array of references
 * @param arr2 - Second array of references
 * @returns True if arrays contain same contentIds
 */
export function areReferencesEqual (
  arr1: ContentReference[] = [],
  arr2: ContentReference[] = [],
): boolean {
  if (arr1.length !== arr2.length) {
    return false
  }

  // Sort both arrays by contentId for comparison
  const sorted1 = [...arr1].sort((a, b) => a.contentId - b.contentId)
  const sorted2 = [...arr2].sort((a, b) => a.contentId - b.contentId)

  // Compare each contentId
  return sorted1.every((item, index) => item.contentId === sorted2[index].contentId)
}

/**
 * Build initial content reference map from GraphQL contentReferencesFrom, grouped by ref types.
 * Shared by all section-managed content reference sections.
 */
export function buildInitialContentReferenceMap (
  referencesFrom: ContentReferencesSectionContentData['contentReferencesFrom'],
  refTypes: string[],
): ContentReferenceMap {
  if (!referencesFrom) return {}
  const validRefs = referencesFrom.filter((ref): ref is NonNullable<typeof ref> => ref != null)
  const allReferences = transformGraphQLToComponent(validRefs)
  const grouped: ContentReferenceMap = {}
  refTypes.forEach((refType) => {
    grouped[refType] = allReferences.filter((ref) => ref.contentReferenceType === refType)
  })
  return grouped
}

/**
 * Whether current references differ from initial (any ref type).
 * Used for dirty state in section-managed sections.
 */
export function isContentReferencesDirty (
  refTypes: string[],
  initial: ContentReferenceMap,
  current: ContentReferenceMap,
): boolean {
  return refTypes.some((refType) => {
    const initialArr = initial[refType] || []
    const currentArr = current[refType] || []
    return !areReferencesEqual(initialArr, currentArr)
  })
}

/**
 * Compute payload for save: which references to delete and which to create.
 * Section hook calls delete then create mutations with this payload.
 */
export function computeContentReferenceSavePayload (
  refTypes: string[],
  contentReferences: ContentReferenceMap,
  initialContentReferences: ContentReferenceMap,
  contentData: ContentReferencesSectionContentData | null,
): ContentReferenceSavePayload {
  const allCurrentReferences: ContentReference[] = []
  const allDeletions: ContentReference[] = []

  refTypes.forEach((refType) => {
    const current = contentReferences[refType] || []
    const initial = initialContentReferences[refType] || []
    const { deletions } = compareReferences(initial, current)
    current.forEach((item) => {
      allCurrentReferences.push({ ...item, contentReferenceType: refType })
    })
    allDeletions.push(...deletions)
  })

  const toCreate: ContentReferenceCreateInput[] = allCurrentReferences.map((item) => ({
    fromContentContentId: contentData?.contentId,
    fromContentContentType: contentData?.contentType,
    toContentContentId: item.contentId,
    toContentContentType: item.contentType,
    type: item.contentReferenceType,
    sortKey: null,
  }))

  return {
    toDelete: allDeletions,
    toCreate,
  }
}
